import { useEffect, useState } from "react";
import { fetchManagerAwayList, setManagerAway, type ManagerAwayListing } from "../lib/api";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Lets an executive manually flag a manager as away for backup-approver
 * routing -- for cases the request-based "away" signal can't see, like a
 * manager who's out sick or travelling without a matching acknowledged
 * request in this system. Complements BackupApprovalsPanel, which only
 * ever reflects what this override (and acknowledged requests) produce.
 */
export default function ManualAwayPanel() {
  const [people, setPeople] = useState<ManagerAwayListing[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  const [draftDates, setDraftDates] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchManagerAwayList()
      .then(setPeople)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load"));
  }, []);

  async function apply(email: string, until: string | null) {
    setBusyEmail(email);
    setError(null);
    try {
      const updated = await setManagerAway(email, until);
      setPeople((prev) => prev?.map((p) => (p.email === email ? updated : p)) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setBusyEmail(null);
    }
  }

  const isCurrentlyAway = (p: ManagerAwayListing) => p.manualAwayUntil !== null && p.manualAwayUntil >= todayIso();

  return (
    <section className="di-card">
      <h2 className="di-h4">Manual away override</h2>
      <p className="di-small">
        For when a manager is genuinely out but the system has no acknowledged request to detect
        it from -- flagging them here opens backup coverage the same as a real request would.
      </p>

      {error && <p className="form-error">{error}</p>}

      {people === null ? (
        <p className="di-small">Loading…</p>
      ) : people.length === 0 ? (
        <p className="di-small empty-state">No managers found.</p>
      ) : (
        <div className="table-scroll">
          <table className="di-table">
            <thead>
              <tr>
                <th>Manager</th>
                <th>Away until</th>
                <th />
              </tr>
            </thead>
          <tbody>
            {people.map((p) => {
              const away = isCurrentlyAway(p);
              const draft = draftDates[p.email] ?? p.manualAwayUntil ?? "";
              return (
                <tr key={p.email}>
                  <td>
                    {p.name} <span className="di-small deny-reason">{p.email}</span>
                    {away && <span className="di-small deny-reason"> — currently flagged away</span>}
                  </td>
                  <td>
                    <input
                      type="date"
                      className="di-input"
                      min={todayIso()}
                      value={draft}
                      disabled={busyEmail === p.email}
                      onChange={(e) =>
                        setDraftDates((prev) => ({ ...prev, [p.email]: e.target.value }))
                      }
                    />
                  </td>
                  <td className="actions-cell">
                    <button
                      className="di-btn di-btn-small di-btn-primary"
                      disabled={busyEmail === p.email || !draft}
                      onClick={() => apply(p.email, draft)}
                    >
                      {busyEmail === p.email ? "Saving…" : "Set"}
                    </button>{" "}
                    <button
                      className="di-btn di-btn-small di-btn-outline"
                      disabled={busyEmail === p.email || !p.manualAwayUntil}
                      onClick={() => {
                        setDraftDates((prev) => ({ ...prev, [p.email]: "" }));
                        apply(p.email, null);
                      }}
                    >
                      Clear
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
